import type {ComponentType} from 'react';

/**
 * Neutral holder for the MarkdownRendererV2 component, mirroring the modelResolverRef pattern.
 *
 * MarkdownRendererV2 is a recursive renderer: it dispatches to sub-renderers (Dropdown, Basic,
 * PassageCorrection, SnippetSelection), and those sub-renderers — along with supplement components
 * (Audio/AudioPlayer, Image, Passage) and question helpers (MarkdownWithSupplement, shared/utils) —
 * need to render nested markdown by recursing back into MarkdownRendererV2. Importing the component
 * directly from those leaves re-forms runtime import cycles (TDZ risk under ESM/SWC).
 *
 * Instead those leaves resolve the component from HERE at render time. MarkdownRendererV2 registers
 * itself when its module evaluates (see the `setMarkdownRenderer` call in MarkdownRendererV2.react).
 * This module imports nothing at runtime, so it sits safely below the renderer and cannot be part of
 * a cycle.
 *
 * Registration is guaranteed by the import graph of whatever code-split route renders the leaf:
 *   - Supplement renderers (Audio/Image/Passage/FreeForm) are reached through `supplementRules` ->
 *     `SupplementRenderer`, i.e. they only render *inside* a MarkdownRendererV2 tree, so the renderer
 *     module has already evaluated.
 *   - The supplement EDITORS (FreeFormEditor, SortableTableEditor, …) are an exception — they render
 *     in the Dennis authoring UI, not inside markdown. They are reached via EditPage, which statically
 *     imports MarkdownRendererV2 (so it is in the same route chunk and registered before they render).
 * If a future route ever renders one of these leaves in a chunk that does NOT transitively import
 * MarkdownRendererV2, `getMarkdownRenderer` will throw — fail loud rather than render wrong. The fix
 * in that case is to ensure that route's entry imports the renderer (an eager bootstrap in main.js is
 * deliberately avoided: it would pull the whole markdown subsystem into the initial bundle and defeat
 * the route-level code splitting).
 */
type MarkdownRendererProps = {
  className?: string;
  dropdowns?: unknown;
  isBeingTranslated?: boolean;
  passageCorrection?: boolean;
  snippetSelectionData?: unknown;
  text?: string;
  highlight?: string;
  [key: string]: unknown;
};

let renderer: ComponentType<MarkdownRendererProps> | null = null;

export function setMarkdownRenderer(component: ComponentType<MarkdownRendererProps>): void {
  renderer = component;
}

export function getMarkdownRenderer(): ComponentType<MarkdownRendererProps> {
  if (!renderer) {
    throw new Error(
      'MarkdownRendererV2 is not registered — ensure generic/MarkdownRendererV2/MarkdownRendererV2.react is loaded before rendering nested markdown'
    );
  }
  return renderer;
}
